import { useCallback, useMemo } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { ContactWorkflowStatus, IContact } from 'clickNgo/entities/contact'
import { TContactGeneralForm } from 'clickNgo/entities/contact'
import { LoadingState } from 'clickNgo/entities/contact'
import { searchPortalContact, updateContact } from 'clickNgo/entities/contact/api'
import { mapContactModifiedValues } from 'clickNgo/entities/contact/api/mapping/mapContactModifiedValues'
import { getJSendErrorMessages, getJSendFieldErrors } from 'clickNgo/helpers/api'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import { I375Form } from 'clickNgo/models/crud/forms'
import {
  resetAllModifiedDataByKey,
  updateInvalidData,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { ContactEditorFormKeys } from '../../../../configs'
import {
  ContactEditorSettingFormFields,
  TContactEditorData,
  TContactEditorHandles,
  TContactEditorUpdateContextProps,
  THandlerResult,
} from '../../../../models'
import { IOnApplyProps } from '../../../contactEditableContainer'
import { prepareValidationResponse } from '../helpers/prepareValidationResponse'

type TParams = Pick<TContactEditorHandles, 'onContactUpdate'> &
  Pick<TContactEditorData, 'contact'> &
  TContactEditorUpdateContextProps

export function useContactBasicInfoUpdate({
  contact,
  source,
  requestPartnerId,
  onContactUpdate,
}: TParams) {
  const dispatch = useAppDispatch()
  const appSource = useAppSource(source || contact.source)
  const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ActualData
  )
  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ErrorMessages
  )
  const [, setIsLoading] = useGetFormDataField<LoadingState>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.IsLoading
  )
  const [generalFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.general)
  const [commonFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.common)
  const [hpRolesFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.hpRoles)

  const modifiedValues = useMemo(
    () => [
      ...generalFormData.modifiedFields,
      ...commonFormData.modifiedFields,
      ...hpRolesFormData.modifiedFields,
    ],
    [commonFormData.modifiedFields, generalFormData.modifiedFields, hpRolesFormData.modifiedFields]
  )

  const hasModifiedValues = modifiedValues.length > 0
  const isValid = generalFormData.isValid && commonFormData.isValid && hpRolesFormData.isValid

  const onApplyHandler = useCallback(
    async ({ workflowStatus }: IOnApplyProps = {}): Promise<THandlerResult> => {
      if (hasModifiedValues && isValid) {
        const isDraft = workflowStatus === ContactWorkflowStatus.Draft
        const resolvedContactId = isDraft
          ? contact.newContactId || contact.contactId
          : contact.contactId

        setIsLoading({ isLoading: true, message: 'Updating contact...' })
        const newContact = {
          ...contact,
          ...generalFormData.formData,
          ...commonFormData.formData,
          ...hpRolesFormData.formData,
          contactId: resolvedContactId,
          partnerId: requestPartnerId || contact.partnerId,
          modifiedValues,
          workflowStatus: workflowStatus || ContactWorkflowStatus.New,
          source: appSource,
        }

        if (!isDraft && modifiedValues.includes('email')) {
          const response = await searchPortalContact(newContact.email)

          if (response.status === OnboardingResponseStatus.Error) {
            setIsLoading({ isLoading: false, message: '' })
            setErrorMessages([`Search Portal: ${response.message || 'unknown error'}`])
            return { hasError: true }
          }

          if (response.status === OnboardingResponseStatus.Fail) {
            setIsLoading({ isLoading: false, message: '' })
            setErrorMessages(getJSendErrorMessages(response))
            return { hasError: true }
          }

          if (response.data.contact !== null) {
            const message = `The email is already in use (contact id: ${response.data.contact.contactId}, partner id: ${response.data.contact.partnerId})`
            setIsLoading({ isLoading: false, message: '' })
            setErrorMessages([message])

            dispatch(
              updateInvalidData({
                field: 'email',
                key: ContactEditorFormKeys.general,
                validationMessage: message,
              })
            )

            return { hasError: true }
          }
        }

        const response = await updateContact(newContact, { forceRoles: true })

        if (response) {
          switch (response.status) {
            case OnboardingResponseStatus.Fail: {
              setIsLoading({ isLoading: false, message: '' })
              setErrorMessages(getJSendErrorMessages(response))
              const invalidData = prepareValidationResponse(getJSendFieldErrors(response))
              invalidData.forEach((data) => dispatch(updateInvalidData(data)))

              return { hasError: true }
            }
            case OnboardingResponseStatus.Error: {
              setIsLoading({ isLoading: false, message: '' })
              setErrorMessages(response.message ? [response.message] : [])
              return { hasError: true }
            }
            case OnboardingResponseStatus.Success: {
              const updatedContact: IContact = {
                ...newContact,
                ...response.data,
                modifiedValues: response.data.modifiedValues
                  ? mapContactModifiedValues(
                      response.data.modifiedValues.split(',') as (keyof I375Form)[]
                    )
                  : newContact.modifiedValues,
                modified: new Date().toISOString(),
              }

              await onContactUpdate(updatedContact)

              updateActualData({
                ...actualData,
                contact: updatedContact,
              })
              break
            }
          }

          setIsLoading({ isLoading: false, message: '' })
          return { hasError: false }
        } else {
          setErrorMessages(['Something went wrong!'])
          setIsLoading({ isLoading: false, message: '' })
          return { hasError: true }
        }
      }
    },
    [
      actualData,
      commonFormData.formData,
      contact,
      dispatch,
      generalFormData.formData,
      hasModifiedValues,
      hpRolesFormData.formData,
      isValid,
      modifiedValues,
      onContactUpdate,
      requestPartnerId,
      setErrorMessages,
      setIsLoading,
      appSource,
      updateActualData,
    ]
  )

  const onCancelHandle = useCallback<() => Promise<THandlerResult>>(async () => {
    dispatch(resetAllModifiedDataByKey(ContactEditorFormKeys.general))
    dispatch(resetAllModifiedDataByKey(ContactEditorFormKeys.common))
    dispatch(resetAllModifiedDataByKey(ContactEditorFormKeys.hpRoles))
  }, [dispatch])

  return { onApplyHandler, hasModifiedValues, onCancelHandle }
}
